import { LiteUIContext } from '@/contexts/LiteUIContext';
import { ScreenContext } from '@/contexts/ScreenContext';
import moment from 'moment';
import { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CdnConfig, CdnContext } from '../contexts/CdnContext';
import { GameConfigContext } from '../contexts/ConfigContext';
import { EnterCountContext } from '../contexts/EnterCountContext';
import { GlobalDataContext } from '../contexts/GlobalDataContext';
import { ZoomFactor } from '../models/Video';
import { GameType as GameTypeEnum } from '../models/games/enums/GameType';
import { getGameState } from '../modules/games/selector';
import { gameSliceActions } from '../modules/games/slice';
import { getHostById } from '../modules/host/slice';
import { getMainUserState } from '../modules/main/selector';
import { getSettingVolumeState } from '../modules/setting/selector';
import { RootState } from '../store/store';
import { GameConfigHostsType } from './useConfig';
import { useCurrentTime } from './useCurrentTime';
import { MessageCommand, sendToParent } from './useIframeMessage';
import { useNavigatorOnLine } from './useNavigatorOnLine';
import { useUserAgent } from './useUserAgent';
export type ResolutionType = 'HD' | 'SD' | 'UD' | 'VideoOff';
export const VIDEO_PLAYER_CONTAINER_ID = 'sa-video-player';
const HighResolution = ['UD', 'HD'] as Array<ResolutionType>;
const LowResolution = ['HD', 'SD'] as Array<ResolutionType>;
const LiteResolution = ['SD', 'SD'] as Array<ResolutionType>;

type OpenPath = {
    VideoPath: string;
};
const getVideoUrlByHostId = (
    config: CdnConfig,
    currentVideoIndex: number,
    hostConfig: GameConfigHostsType,
    hostId: number,
    vendor: string,
    quality: ResolutionType,
    dbTime: number
): string | undefined => {
    if (quality === 'VideoOff') return undefined;
    if (hostConfig.sai) {
        if (dbTime < hostConfig.sai) return undefined;
    }
    const desk = `desk${hostId}${quality.toLocaleLowerCase()}`;
    try {
        if (config.myVideoChannel && config.myVideoChannel.length > 0) {
            const cur =
                config.myVideoChannel[
                    currentVideoIndex % config.myVideoChannel.length
                ];
            if (cur.type === 'FlvJs') {
                const ivsChannel = config.ivsChannel[hostId]; //for SAI if no IVS not to play flv
                const flvChannel = config.flvChannel.find(c =>
                    c.group.includes(hostConfig.group)
                )?.name;
                if (flvChannel && ivsChannel) {
                    const baseFlvUrl = cur.url.replace('$a', flvChannel);
                    return baseFlvUrl.replace('$d', desk);
                }
            } else if (cur.type === 'AWS_IVS') {
                const ivsChannel = config.ivsChannel[hostId];
                if (ivsChannel && ivsChannel[quality])
                    return cur.url.replace('$s', ivsChannel[quality]);
                /*
                const hlsChannel = config.hlsChannel.find(c => c.group.includes(hostConfig.group))?.name;
                if (hlsChannel) {
                    const baseHlsUrl = config.hlsUrl.replace('$a', hlsChannel);
                    return baseHlsUrl.replace('$h', desk);
                }
                */
            } else if (cur.type === 'CMAFHlsjs') {
                const hlsChannel = config.hlsChannel.find(c =>
                    c.group.includes(hostConfig.group)
                )?.name;
                if (hlsChannel) {
                    const baseHlsUrl = cur.url.replace('$a', hlsChannel);
                    return baseHlsUrl.replace('$h', desk);
                }
            }
        }

        return undefined;
    } catch (err) {
        console.warn(
            'app::video::function',
            'cannot get video url of host:',
            hostId,
            vendor,
            quality,
            err
        );
        return undefined;
    }
};
export const getZoomFactor = (
    gameType: GameTypeEnum = GameTypeEnum.None,
    isZoom: boolean = false,
    isMultiBet: boolean = false
): ZoomFactor => {
    const f = (x1: number, x2: number, x3: number) =>
        ({ u: x1, v: x2, scale: x3 }) as ZoomFactor;
    const raw = f(0.5, 0, 1);
    console.log('app::video::function', 'zoom', gameType, isMultiBet);
    if (gameType !== GameTypeEnum.None) {
        switch (gameType) {
            case GameTypeEnum.Baccarat:
                return isZoom ? f(0.5, 0.5, 2.368) : f(0.5, 0, 1.48);
            case GameTypeEnum.SqueezeBaccarat:
                return isZoom ? f(0.5, 0.5, 2.368) : f(0.5, 0, 1.48);
            case GameTypeEnum.Dragon:
                return isZoom ? f(0.5, 0.6, 2.4) : f(0.5, 0, 1.2);
            case GameTypeEnum.PokDeng:
                return isZoom ? f(0.5, 0.7, 1.8) : f(0.5, 0, 1.11);
            case GameTypeEnum.Blackjack:
                return isZoom ? f(0.5, 0.5, 1.15) : f(0.5, 0, 1.15);
            case GameTypeEnum.TeenPatti2020:
                return isZoom ? f(0.5, 0.55, 2.4) : f(0.5, 0, 1.35);
            case GameTypeEnum.Roulette:
                return isZoom ? f(0.5, 1, 2.5) : f(0.5, 0, 1);
        }
    }
    return raw;
};
const useVideoPlayer = (hostId: number) => {
    const {
        device: { vendor },
    } = useUserAgent();
    const { GameType, IsRest } = useSelector((state: RootState) =>
        getHostById(state, hostId)
    );
    const { Username } = useSelector(getMainUserState);
    const { Studio } = useSelector(getSettingVolumeState);
    // ivs
    const { innerWidth } = window;
    const player = useRef<MultiPlayer.VideoPlayer>();
    const cdnConfig = useContext(CdnContext);
    const gameConfig = useContext(GameConfigContext);
    const {
        resolution,
        setResolution,
        resolutionIdx,
        setResolutionIdx,
        videoOff,
        videoMBOff,
        countryCode,
        isMultiBet,
    } = useContext(GlobalDataContext); //Issue List #104
    const [isAudioBlocked, setIsAudioBlocked] = useState<boolean>(false);
    const [isVideoOff, setIsVideoOff] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [zIndex, setZIndex] = useState<number>(1000);
    const [isShowEmoji, setIsShowEmoji] = useState(false);
    const { isLite } = useContext(LiteUIContext);
    // const [isMBEnlarge, setIsMBEnlarge] = useState<boolean>(false);

    const { isMobile } = useContext(ScreenContext);
    const [supportResolution, setSupportResolution] =
        useState<Array<ResolutionType>>();
    const [zoomFactor, setZoomFactor] = useState<ZoomFactor>({
        u: 0.5,
        v: 0.5,
        scale: 1,
    });
    const [width, setWidth] = useState(0);
    const [height, setHeight] = useState(0);
    const [retry, setRetry] = useState(0);
    const [refresh, setRefresh] = useState(0);
    const dispatch = useDispatch();
    const { online: socketOnline, currentVideoIndex } =
        useSelector(getGameState);
    const onlineState = useNavigatorOnLine();

    const [initTime, setInitTime] = useState(0);
    const [playTime, setPlayTime] = useState(0);
    const { enterCount, addCount } = useContext(EnterCountContext);

    const [clientWidth, setClientWidth] = useState(0);
    const [clientHeight, setClientHeight] = useState(0);

    const notToReconnectVideo = useRef<boolean>(false);
    const currentVideoPath = useRef<string>();

    const resetTime = () => {
        setInitTime(0);
        setPlayTime(0);
    };
    useEffect(() => {
        if (isLite) {
            setSupportResolution(LiteResolution);
        } else if (!isMobile) {
            const tmp = cdnConfig.ivsChannel[hostId];
            if (tmp && tmp.UD !== undefined) {
                setSupportResolution(HighResolution);
            } else {
                setSupportResolution(LowResolution);
            }
        } else {
            setSupportResolution(LowResolution);
        }
    }, [isMobile, hostId]);
    useEffect(() => {
        console.log('app::video::resolution', supportResolution);
        if (supportResolution && supportResolution.length > 0) {
            changeResolution(supportResolution[resolutionIdx]);
        }
    }, [supportResolution]);
    useEffect(() => {
        document.documentElement.style.setProperty(
            '--video-layer-index',
            `${zIndex}`
        );
    }, [zIndex]);
    useEffect(() => {
        if (playTime > 0 && initTime > 0) {
            const d = {
                hostId,
                game: initTime,
                video: playTime - initTime,
                count: enterCount,
            };
            sendToParent({
                type: MessageCommand.VIDEO_PERFORMANCE,
                data: JSON.stringify(d),
            });
            addCount();
            resetTime();
        }
    }, [playTime, initTime]);

    const { currentTimeStamp } = useCurrentTime();
    const resumeVideo = () => {
        setRetry(v => v + 1);
    };
    useEffect(() => {
        doRefresh();
    }, [refresh]);
    useEffect(() => {
        console.log('app::video::channel::', resolution);
    }, [resolution]);
    const initialPlayer = () => {
        if (notToReconnectVideo.current) return;
        if (supportResolution === undefined) return;
        if (supportResolution[resolutionIdx] !== resolution) return;
        if (IsRest !== true && IsRest !== undefined)
            try {
                if (document.getElementById(VIDEO_PLAYER_CONTAINER_ID)) {
                    setInitTime(moment().valueOf());
                    // video time
                    const dbTime = currentTimeStamp();
                    const TimestampInDBTimeZone =
                        Math.floor(dbTime / 1000) - 60 * 60 * 8; // TODO: issue #218
                    MultiPlayer.VideoPlayer.SetUsername(Username);
                    MultiPlayer.VideoPlayer.SetServerTime(
                        TimestampInDBTimeZone
                    );

                    const hostConfig = gameConfig.hosts.hostList.find(
                        h => h.hostId === hostId
                    );
                    const streamUrl = getVideoUrlByHostId(
                        cdnConfig,
                        currentVideoIndex,
                        hostConfig ?? ({} as GameConfigHostsType),
                        hostId,
                        vendor ?? 'Unknown',
                        resolution,
                        dbTime
                    );

                    console.log(
                        'app::video::channel',
                        vendor,
                        streamUrl,
                        resolution,
                        retry,
                        refresh,
                        !videoOff
                    );
                    if (streamUrl) {
                        currentVideoPath.current = streamUrl;
                        setIsAudioBlocked(false);
                        MultiPlayer.VideoPlayer.SetCountry(countryCode);
                        player.current = MultiPlayer.VideoPlayer.GetInstance();
                        player.current.EnableAutoFocusOutHandle(isMobile);
                        player.current.SetMountTargetID(
                            VIDEO_PLAYER_CONTAINER_ID
                        );
                        player.current.Play(streamUrl, Studio / 100);
                        player.current.on(
                            MultiPlayer.Event.VideoEvent.Play,
                            onVideoPlay
                        );
                        player.current.on(
                            MultiPlayer.Event.VideoEvent.Error,
                            OnPlayerError
                        );
                        player.current.on(
                            MultiPlayer.Event.VideoEvent.AudioBlock,
                            OnPlayerAudioBlock
                        );
                        player.current.on(
                            MultiPlayer.Event.VideoEvent.Open,
                            OnVideoOpen
                        );

                        dispatch(gameSliceActions.setLastVideo(streamUrl));
                    }
                }
            } catch (ex) {
                console.error(
                    'app::video::error',
                    cdnConfig.videoPlayerUrl,
                    ex
                );
            }
    };
    const killPlayer = () => {
        console.log('app::video::function', 'killPlayer');
        player.current?.Destroy();
    };
    const changeResolutionIdx = (typeIdx: number) => {
        console.log('app::video::function', 'changeResolutionIdx', typeIdx);
        setResolutionIdx(typeIdx);
        if (supportResolution) {
            setResolution(supportResolution[typeIdx]);
        }
        setRefresh(v => v + 1);
    };
    const changeResolution = (type: ResolutionType) => {
        if (type == resolution) return;
        console.log('app::video::function', 'changeResolution', type);
        setResolution(type);
        if (supportResolution) {
            setResolutionIdx(supportResolution?.indexOf(type));
        }
        setRefresh(v => v + 1);
    };
    const changeVolume = (volume: number) => {
        // console.log('app::video::function', 'changeVolume', volume);
        player.current?.SetVolume(volume);
    };
    const audioResume = () => {
        console.log(
            'app::video::function',
            'resume',
            'player volume: ',
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            (<any>player.current)?.VP.Volume,
            'client control volume:',
            Studio / 100
        );
        player.current?.AudioResume();
    };
    const doRefresh = () => {
        const video = isMultiBet ? videoMBOff && videoOff : videoOff;
        if (!video) {
            changeIsLoading(true);
            console.log('app::video::function', 'refresh');
            if (player.current) {
                player.current.off(
                    MultiPlayer.Event.VideoEvent.Play,
                    onVideoPlay
                );
                player.current.off(
                    MultiPlayer.Event.VideoEvent.Error,
                    OnPlayerError
                );
                player.current.off(
                    MultiPlayer.Event.VideoEvent.AudioBlock,
                    OnPlayerAudioBlock
                );
                player.current.off(
                    MultiPlayer.Event.VideoEvent.Open,
                    OnVideoOpen
                );

                player.current.EnableAutoFocusOutHandle(false);
                initialPlayer();
            }
        }
    };
    const updateClientSize = () => {
        const videoEl = document.querySelector(
            `#${VIDEO_PLAYER_CONTAINER_ID} video,canvas`
        );
        if (videoEl) {
            setClientWidth(videoEl.clientWidth);
            setClientHeight(videoEl.clientHeight);
        }
    };
    const zoom = (zf: ZoomFactor) => {
        if (player.current) {
            setZoomFactor(zf);
        }
    };
    const onVideoPlay = () => {
        if (videoOff || (videoMBOff && isMultiBet)) {
            if (player.current) {
                console.log(`app::video::channel::video::destroy`);
                player.current.Destroy();
                player.current = undefined;
            }
        } else {
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            // const ins = player.current as any;
            // console.log('app::video::function', 'onVideoPlay::VP.Volume: ', ins?.VP.Volume);
            updateClientSize();
            changeVideoOff(false);
            changeIsLoading(false);
            setPlayTime(moment().valueOf());
            setRetry(0);
        }
    };
    const OnVideoOpen = (path: OpenPath) => {
        if (path && currentVideoPath.current !== path.VideoPath) {
            console.log(
                `app::video::channel::video::wrong::${path.VideoPath}::`
            );
            player.current?.Destroy();
            setRefresh(v => v + 1);
        }
    };

    const OnPlayerError = () => {
        dispatch(
            gameSliceActions.setCurrentVideoIndex({
                currentVideoIndex: currentVideoIndex + 1,
            })
        );
    };

    const OnPlayerAudioBlock = () => {
        setIsAudioBlocked(true);
    };

    const changeVideoOff = (b: boolean) => {
        setIsVideoOff(b);
    };
    const changeIsLoading = (b: boolean) => {
        setIsLoading(b);
    };

    useEffect(() => {
        player.current?.EnableAutoFocusOutHandle(false);
        setRetry(v => v + 1);
    }, [currentVideoIndex]);
    useEffect(() => {
        if (!socketOnline || !onlineState) {
            notToReconnectVideo.current = true;
            if (player.current) killPlayer();
        }
    }, [socketOnline, onlineState]);
    useEffect(() => {
        if (player.current) killPlayer();
    }, [hostId]);
    useEffect(() => {
        if (retry === 0 || retry > 10) {
            return;
        }
        if (isMultiBet) {
            if (!videoMBOff && !videoOff) initialPlayer();
        } else {
            if (!videoOff) initialPlayer();
        }
    }, [retry]);
    useEffect(() => {
        if (isMultiBet) {
            if (!videoMBOff && !videoOff) initialPlayer();
        } else {
            if (!videoOff) initialPlayer();
        }
        return () => {
            player.current?.off(MultiPlayer.Event.VideoEvent.Open, OnVideoOpen);
            player.current?.off(MultiPlayer.Event.VideoEvent.Play, onVideoPlay);
            player.current?.EnableAutoFocusOutHandle(false);
            player.current?.Stop();
            // changeVideoOff(true);
            changeIsLoading(true);
        };
    }, [hostId, IsRest, resolution, videoOff, videoMBOff]);
    useEffect(() => {
        const w = Number(innerWidth);
        setWidth(w);
        setHeight((w / 16) * 9);
    }, [innerWidth]);
    useEffect(() => {
        const zf = getZoomFactor(GameType);
        console.log('zoom::debug', hostId, GameType, zf);
        zoom(zf);
    }, [GameType, videoOff]); //handle switch table -> set default video size
    // useEffect(() => {
    //     const volume = Studio / 100;
    //     console.log('app::video::volume::change', volume);
    //     player.current?.SetVolume(volume);
    // }, [Studio]);
    useEffect(() => {
        setZoomFactor(getZoomFactor(GameType));
        return () => {
            console.log('app::video::volume::destroy');
            player.current?.Destroy();
        };
    }, []);
    return {
        hostId,
        width,
        height,
        resolution,
        resolutionIdx,
        zoomFactor,
        isVideoOff,
        isLoading,
        isAudioBlocked,
        zIndex,
        playTime,
        clientWidth,
        clientHeight,
        isShowEmoji,
        // isMBEnlarge,
        supportResolution,
        changeResolution,
        changeResolutionIdx,
        changeVideoOff,
        changeIsLoading,
        doRefresh,
        audioResume,
        killPlayer,
        zoom,
        setZIndex,
        setIsShowEmoji,
        resumeVideo,
        // setIsMBEnlarge,
        changeVolume,
    };
};
export default useVideoPlayer;
export type useVideoPlayerState = ReturnType<typeof useVideoPlayer>;
